'use client'

import { useState, useEffect } from 'react'
import { GlobalSidebar } from '@/components/ui/global-sidebar'
import { useTranslations, useLocale } from 'next-intl'
import {
  ChevronDown,
  ChevronRight,
  Filter,
  RefreshCw,
  Check,
  Target,
  FileText,
  Package,
  FolderTree
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'

interface FilterOption {
  id: number
  name: string
}

interface Project {
  id: number
  name: string
  identifier: string
}

interface RoadmapFilterSidebarProps {
  isOpen: boolean
  onClose: () => void
  trackers: FilterOption[]
  selectedTrackers: Set<number>
  onTrackerToggle: (trackerId: number) => void
  versions: FilterOption[]
  selectedVersions: Set<number>
  onVersionToggle: (versionId: number) => void
  showCompletedVersions: boolean
  onShowCompletedChange: (show: boolean) => void
  subprojects: Project[]
  selectedSubprojects: Set<number>
  onSubprojectToggle: (projectId: number) => void
  onResetFilters: () => void
}

export function RoadmapFilterSidebar({
  isOpen,
  onClose,
  trackers,
  selectedTrackers,
  onTrackerToggle,
  versions,
  selectedVersions,
  onVersionToggle,
  showCompletedVersions,
  onShowCompletedChange,
  subprojects,
  selectedSubprojects,
  onSubprojectToggle,
  onResetFilters
}: RoadmapFilterSidebarProps) {
  const [mounted, setMounted] = useState(false)
  const t = useTranslations('roadmapPage')
  const c = useTranslations('common')
  const locale = useLocale()
  const isRTL = locale === 'ar' || locale === 'ur'
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['trackers', 'versions', 'subprojects'])
  )

  useEffect(() => {
    setMounted(true)
    return () => setMounted(false)
  }, [subprojects])

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev)
      if (newSet.has(section)) {
        newSet.delete(section)
      } else {
        newSet.add(section)
      }
      return newSet
    })
  }

  const activeFiltersCount = 
    (selectedTrackers.size < trackers.length && selectedTrackers.size > 0 ? selectedTrackers.size : 0) +
    (selectedVersions.size < versions.length && selectedVersions.size > 0 ? selectedVersions.size : 0) +
    (showCompletedVersions ? 1 : 0) +
    (selectedSubprojects.size > 0 ? selectedSubprojects.size : 0)

  return (
    <GlobalSidebar 
      isOpen={isOpen} 
      onClose={onClose} 
      title="Filters"
    >
      <ScrollArea className="h-full -mx-6 px-6">
        <div className="p-2">
          {/* Reset Button */}
          {activeFiltersCount > 0 && (
            <div className="mb-4 flex items-center justify-between px-2">
              <Badge variant="default" className="h-5 px-2 text-xs">
                {activeFiltersCount} active
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={onResetFilters}
                className="h-7 text-xs"
              >
                <RefreshCw className={cn('w-3 h-3', isRTL ? 'ml-1' : 'mr-1')} />
                Reset All
              </Button>
            </div>
          )}
          
          {/* Tracker Filters */}
          {trackers.length > 0 && (
            <div className="mb-1">
              <button
                onClick={() => toggleSection('trackers')}
                className={cn(
                  "w-full flex items-center justify-between px-3 py-2.5",
                  "hover:bg-muted/50 rounded-md transition-colors",
                  "text-sm font-medium group"
                )}
              >
                <div className="flex items-center gap-2">
                  {expandedSections.has('trackers') ? (
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  )}
                  <FileText className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  <span className="text-foreground">Trackers</span>
                </div>
                {selectedTrackers.size > 0 && selectedTrackers.size < trackers.length && (
                  <Badge variant="default" className="h-5 px-1.5 text-xs">
                    {selectedTrackers.size}
                  </Badge>
                )}
              </button>

              {expandedSections.has('trackers') && (
                <div className={cn('mt-1 mb-2 space-y-0.5', isRTL ? 'mr-4 ml-1' : 'ml-4 mr-1')}>
                  {trackers.map((tracker, index) => {
                    const isSelected = selectedTrackers.has(tracker.id)
                    return (
                      <label
                        key={`tracker-${tracker.id}-${index}`}
                        className={cn(
                          "flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer",
                          "hover:bg-muted/50 transition-colors group",
                          isSelected && "bg-primary/5 hover:bg-primary/10"
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => onTrackerToggle(tracker.id)}
                          className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary focus:ring-offset-0 cursor-pointer"
                        />
                        <span className={cn(
                          "text-sm flex-1",
                          isSelected ? "text-foreground font-medium" : "text-muted-foreground group-hover:text-foreground"
                        )}>
                          {tracker.name}
                        </span>
                        {isSelected && (
                          <Check className="w-4 h-4 text-primary" />
                        )}
                      </label>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {/* Version Filters */}
          {versions.length > 0 && (
            <div className="mb-1">
              <button
                onClick={() => toggleSection('versions')}
                className={cn(
                  "w-full flex items-center justify-between px-3 py-2.5",
                  "hover:bg-muted/50 rounded-md transition-colors",
                  "text-sm font-medium group"
                )}
              >
                <div className="flex items-center gap-2">
                  {expandedSections.has('versions') ? (
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  )}
                  <Target className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  <span className="text-foreground">Versions</span>
                </div>
                {selectedVersions.size > 0 && selectedVersions.size < versions.length && (
                  <Badge variant="default" className="h-5 px-1.5 text-xs">
                    {selectedVersions.size}
                  </Badge>
                )}
              </button>

              {expandedSections.has('versions') && (
                <div className={cn('mt-1 mb-2 space-y-0.5', isRTL ? 'mr-4 ml-1' : 'ml-4 mr-1')}>
                  {versions.map((version, index) => {
                    const isSelected = selectedVersions.has(version.id)
                    return (
                      <label
                        key={`version-${version.id}-${index}`}
                        className={cn(
                          "flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer",
                          "hover:bg-muted/50 transition-colors group",
                          isSelected && "bg-primary/5 hover:bg-primary/10"
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => onVersionToggle(version.id)}
                          className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary focus:ring-offset-0 cursor-pointer"
                        />
                        <span className={cn(
                          "text-sm flex-1",
                          isSelected ? "text-foreground font-medium" : "text-muted-foreground group-hover:text-foreground"
                        )}>
                          {version.name}
                        </span>
                        {isSelected && (
                          <Check className="w-4 h-4 text-primary" />
                        )}
                      </label>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {/* Version Status */}
          <div className="mb-1">
            <button
              onClick={() => toggleSection('status')}
              className={cn(
                "w-full flex items-center justify-between px-3 py-2.5",
                "hover:bg-muted/50 rounded-md transition-colors",
                "text-sm font-medium group"
              )}
            >
              <div className="flex items-center gap-2">
                {expandedSections.has('status') ? (
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                )}
                <Package className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                <span className="text-foreground">Version Status</span>
              </div>
              {showCompletedVersions && (
                <Badge variant="default" className="h-5 px-1.5 text-xs">
                  1
                </Badge>
              )}
            </button>

            {expandedSections.has('status') && (
              <div className={cn('mt-1 mb-2 space-y-0.5', isRTL ? 'mr-4 ml-1' : 'ml-4 mr-1')}>
                <label
                  className={cn(
                    "flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer",
                    "hover:bg-muted/50 transition-colors group",
                    showCompletedVersions && "bg-primary/5 hover:bg-primary/10"
                  )}
                >
                  <input
                    type="checkbox"
                    checked={showCompletedVersions}
                    onChange={(e) => onShowCompletedChange(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary focus:ring-offset-0 cursor-pointer"
                  />
                  <span className={cn(
                    "text-sm flex-1",
                    showCompletedVersions ? "text-foreground font-medium" : "text-muted-foreground group-hover:text-foreground"
                  )}>
                    Show completed versions
                  </span>
                  {showCompletedVersions && (
                    <Check className="w-4 h-4 text-primary" />
                  )}
                </label>
              </div>
            )}
          </div>

          {/* Subprojects */}
          {subprojects.length > 0 && (
            <div className="mb-1">
              <button
                onClick={() => toggleSection('subprojects')}
                className={cn(
                  "w-full flex items-center justify-between px-3 py-2.5",
                  "hover:bg-muted/50 rounded-md transition-colors",
                  "text-sm font-medium group"
                )}
              >
                <div className="flex items-center gap-2">
                  {expandedSections.has('subprojects') ? (
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  )}
                  <FolderTree className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  <span className="text-foreground">Subprojects</span>
                </div>
                {selectedSubprojects.size > 0 && (
                  <Badge variant="default" className="h-5 px-1.5 text-xs">
                    {selectedSubprojects.size}
                  </Badge>
                )}
              </button>

              {expandedSections.has('subprojects') && (
                <div className={cn('mt-1 mb-2 space-y-0.5', isRTL ? 'mr-4 ml-1' : 'ml-4 mr-1')}>
                  {subprojects.map((project, index) => {
                    const isSelected = selectedSubprojects.has(project.id)
                    return (
                      <label
                        key={`subproject-${project.id}-${index}`}
                        className={cn(
                          "flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer",
                          "hover:bg-muted/50 transition-colors group",
                          isSelected && "bg-primary/5 hover:bg-primary/10"
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => onSubprojectToggle(project.id)}
                          className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary focus:ring-offset-0 cursor-pointer"
                        />
                        <span className={cn(
                          "text-sm flex-1",
                          isSelected ? "text-foreground font-medium" : "text-muted-foreground group-hover:text-foreground"
                        )}>
                          {project.name}
                        </span>
                        {isSelected && (
                          <Check className="w-4 h-4 text-primary" />
                        )}
                      </label>
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </ScrollArea>
    </GlobalSidebar>
  )
}
